import { useMemo, useRef } from 'react';
import { useTheme } from '@simplywallst/ui-core';
import WaterfallChart from '@/components/WaterfallChart';
import { useOverviewTranslation } from '@/pages/CompanyReport/components/OverviewSection/hooks/useOverviewTranslation';
import type { useCompanyStock } from '../../hooks/useCompanyStock';

import { useCompany } from '@/hooks/useCompany';
import { toCurrencyTruncated } from '@/utilities/formatting';

export function IncomeWaterfall({
  details: _details,
  canonicalUrl,
}: {
  details: ReturnType<typeof useCompanyStock>['details'];
  canonicalUrl: string;
}) {
  const theme = useTheme();
  const chartsRef = useRef(['Income Statement']);
  const { current: chart } = chartsRef;

  const t = useOverviewTranslation();

  const keyRef = useRef([
    {
      key: 'revenue',
      label: t('fundamentals.income.chart.revenue'),
    },
    {
      key: 'costOfRevenue',
      label: t('fundamentals.income.chart.costOfRevenue'),
    },
    {
      key: 'grossProfit',
      label: t('fundamentals.income.chart.grossProfit'),
    },
    {
      key: 'otherExpenses',
      label: t('fundamentals.income.chart.other'),
    },
    {
      key: 'earnings',
      label: t('fundamentals.income.chart.earnings'),
    },
  ]);

  const { current: keys } = keyRef;
  const memoedKeys = useMemo(() => keys.map((k) => k.key), [keys]);
  const memoedLabels = useMemo(() => keys.map((k) => k.label), [keys]);

  // TODO: use useCompanyStock instead once values are available in GraphQL
  const { data } = useCompany(canonicalUrl);

  const pastData =
    data?.data?.analysis?.data.extended?.data.raw_data?.data.past;
  const latestReportingPastData =
    pastData && Object.keys(pastData).length > 0
      ? Object.values(pastData)[Object.values(pastData).length - 1]
      : {};

  const reportingCurrencyIso =
    data?.data?.analysis?.data.extended?.data.raw_data?.data.currency_info
      .reporting_currency_iso;

  const revenue =
    (latestReportingPastData?.total_rev?.value ?? 0) *
    (latestReportingPastData?.total_rev?.unit_numeric ?? 0);
  const costOfRevenue =
    (latestReportingPastData?.cogs?.value ?? 0) *
    (latestReportingPastData?.cogs?.unit_numeric ?? 0);
  const grossProfit =
    (latestReportingPastData?.gross_profit?.value ?? 0) *
    (latestReportingPastData?.gross_profit?.unit_numeric ?? 0);
  const earnings =
    (latestReportingPastData?.ni_avail_excl?.value ?? 0) *
    (latestReportingPastData?.ni_avail_excl?.unit_numeric ?? 0);
  const expenses = grossProfit - earnings;

  const revenueFormatted = toCurrencyTruncated(
    revenue,
    1,
    reportingCurrencyIso
  );
  const costOfRevenueFormatted = toCurrencyTruncated(
    costOfRevenue,
    1,
    reportingCurrencyIso
  );
  const grossProfitFormatted = toCurrencyTruncated(
    grossProfit,
    1,
    reportingCurrencyIso
  );
  const expensesFormatted = toCurrencyTruncated(
    expenses,
    1,
    reportingCurrencyIso
  );
  const earningsFormatted = toCurrencyTruncated(
    earnings,
    1,
    reportingCurrencyIso
  );

  const entities = {
    [chart[0]]: {
      [keys[0].key]: revenue,
      [keys[1].key]: costOfRevenue,
      [keys[2].key]: grossProfit,
      [keys[3].key]: expenses,
      [keys[4].key]: earnings,
    },
  };

  const entitiesFormatted = Object.assign({
    [chart[0]]: {
      [keys[0].key]: revenueFormatted,
      [keys[1].key]: costOfRevenueFormatted,
      [keys[2].key]: grossProfitFormatted,
      [keys[3].key]: expensesFormatted,
      [keys[4].key]: earningsFormatted,
    },
  });

  const entitiesTooltips = {
    [chart[0]]: {
      [keys[0].key]: t('fundamentals.income.chart.revenue'),
      [keys[1].key]: t('fundamentals.income.chart.costOfRevenue'),
      [keys[2].key]: t('fundamentals.income.chart.grossProfit'),
      [keys[3].key]: t('fundamentals.income.chart.other'),
      [keys[4].key]: t('fundamentals.income.chart.earnings'),
    },
  };
  const entitiesProps = {
    [chart[0]]: {
      [keys[0].key]: {},
      [keys[1].key]: {
        showTopBar: true,
        renderHeight: revenue > grossProfit ? revenue : grossProfit,
        negativeValueColor: theme.color.badRGBA(0.5),
      },
      [keys[2].key]: {},
      [keys[3].key]: {
        showTopBar: true,
        renderHeight: grossProfit > earnings ? grossProfit : earnings,
        negativeValueColor: theme.color.badRGBA(0.5),
      },
      [keys[4].key]: {},
    },
  };

  return (
    <WaterfallChart
      hideTitle
      canStack={false}
      charts={chart}
      colors={theme.chart.barChart.incomeStatement.colors}
      entities={entities}
      entitiesFormatted={entitiesFormatted}
      entitiesTooltips={entitiesTooltips}
      typography="tinyStrong"
      height={160}
      chartHeight={250}
      keys={memoedKeys}
      labels={memoedLabels}
      titleColors={theme.chart.barChart.incomeStatement.titleColors}
      entitiesProps={entitiesProps}
    />
  );
}
